import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, BACKEND_URL } from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { SPONSOR, sponsorMatchesTrade as matchSponsor } from '@/constants/sponsor';
import { useGuide, useAuth } from '@/context/AuthContext';
import { useBuddyVoice } from '@/lib/voice';
import CoreyAvatar from '@/components/CoreyAvatar';
import { trackSponsorClick } from '@/lib/tracking';
import CallProModal from '@/components/CallProModal';
import TheorySection from '@/components/TheorySection';
import {
  Loader2, AlertTriangle, Clock, DollarSign, Gauge, ShieldAlert,
  Wrench, Package, ArrowLeft, Phone, PhoneCall, Zap, ChevronDown, ChevronUp, MapPin, Tag,
  Sparkles, Heart, Video, Play, RefreshCw, Sliders, X, Hammer, FileText, Plus,
} from 'lucide-react';

export default function SolutionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProModal, setShowProModal] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [videoElapsedSec, setVideoElapsedSec] = useState(0);
  const [showTuner, setShowTuner] = useState(false);
  const [vSetting, setVSetting] = useState('');
  const [vFocus, setVFocus] = useState('');
  const [vNotes, setVNotes] = useState('');
  const [err, setErr] = useState('');
  const guide = useGuide();
  const { speakQuip, stop: stopVoice } = useBuddyVoice();
  const quipFiredRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/problems/${id}`);
        setProblem(res.data);
        if (res.data.video_status === 'ready' && res.data.video_url) {
          setVideoUrl(`${BACKEND_URL}${res.data.video_url}`);
        }
        // Buddy greets the user with a contextual line about this exact fix.
        // Guard against React StrictMode double-invoke to avoid double TTS cost.
        const s = res.data?.solution;
        if (s && !quipFiredRef.current) {
          quipFiredRef.current = true;
          speakQuip('solution_loaded', {
            title: s.title,
            trade: s.trade,
            difficulty: s.difficulty,
            is_advanced: s.is_advanced,
          });
        }
      } catch (e) {
        setErr(e.response?.data?.detail || 'Failed to load solution');
      } finally {
        setLoading(false);
      }
    })();
    return () => stopVoice();
  }, [id, speakQuip, stopVoice]);

  // Elapsed timer during video generation
  useEffect(() => {
    if (!videoLoading) return;
    setVideoElapsedSec(0);
    const t = setInterval(() => setVideoElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [videoLoading]);

  const generateVideo = async ({ regenerate = false } = {}) => {
    setVideoError('');
    setVideoLoading(true);
    if (regenerate) setVideoUrl(null);
    try {
      const body = {
        setting: vSetting || null,
        focus: vFocus || null,
        notes: vNotes.trim() || null,
        regenerate,
      };
      const res = await api.post(`/problems/${id}/video`, body, { timeout: 15 * 60 * 1000 });
      setVideoUrl(`${BACKEND_URL}${res.data.video_url}?t=${Date.now()}`);
      setShowTuner(false);
    } catch (e) {
      setVideoError(e.response?.data?.detail || 'Video generation failed. Corey may be tied up — try again in a bit.');
    } finally {
      setVideoLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-amber-500" />
      </div>
    );
  }
  if (err || !problem) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="text-red-400 font-mono">{err || 'Not found'}</div>
        <Link to="/app" className="btn-secondary mt-6 inline-flex">Back</Link>
      </div>
    );
  }

  const s = problem.solution;
  const sponsorMatchesTrade = matchSponsor(s.trade);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <button
        onClick={() => navigate('/app')}
        data-testid={IDS.solutionBackBtn}
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-widest mb-6"
      >
        <ArrowLeft size={14} /> New Problem
      </button>

      {/* Advanced flashing warning */}
      {s.is_advanced && (
        <div
          data-testid={IDS.solutionAdvancedBadge}
          className="advanced-warning p-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 border border-red-500"
        >
          <div className="flex items-center gap-2 text-red-400 shrink-0">
            <ShieldAlert size={22} strokeWidth={2.5} />
            <span className="font-display text-xl tracking-wide">ADVANCED</span>
          </div>
          <div className="text-zinc-100 text-sm flex-1">
            {s.advanced_warning || 'This job involves real risk. Proceed carefully — or call a pro.'}
          </div>
          {sponsorMatchesTrade && (
            <a
              href={SPONSOR.phoneHref}
              onClick={() => trackSponsorClick('advanced_badge', { trade: s.trade, problemId: problem.id })}
              className="btn-primary text-xs whitespace-nowrap"
            >
              <Phone size={14} /> {SPONSOR.phone}
            </a>
          )}
        </div>
      )}

      {/* Title */}
      <div className="mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500 mb-2">
          /{s.trade.toLowerCase().replace(/\s+/g, '_')}
        </div>
        <h1
          data-testid={IDS.solutionTitle}
          className="font-display text-4xl sm:text-5xl text-zinc-100 tracking-tight leading-tight"
        >
          {s.title}
        </h1>
        <p data-testid={IDS.solutionSummary} className="text-zinc-400 mt-3 text-lg">
          {s.summary}
        </p>

        {/* Likely causes — kept subtle for beginners */}
        {Array.isArray(s.likely_causes) && s.likely_causes.length > 0 && (
          <div data-testid="likely-causes" className="mt-5 border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2">
              Most likely cause
            </div>
            <div className="text-zinc-100 text-base">
              <span className="font-semibold">{s.likely_causes[0].cause}</span>
              <span className="text-lime-400 font-mono text-sm ml-2">({s.likely_causes[0].percent}%)</span>
            </div>
            {s.likely_causes[0].note && (
              <div className="text-zinc-500 text-xs mt-1 italic">{s.likely_causes[0].note}</div>
            )}
            {s.likely_causes.length > 1 && (
              <details className="mt-3 group">
                <summary className="cursor-pointer text-zinc-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-widest">
                  Also possible &rarr;
                </summary>
                <ul className="mt-2 space-y-1">
                  {s.likely_causes.slice(1).map((c, i) => (
                    <li key={`cause-${i}`} className="text-zinc-400 text-sm">
                      <span className="text-zinc-300">{c.cause}</span>
                      <span className="text-zinc-500 font-mono text-xs ml-2">({c.percent}%)</span>
                      {c.note && <div className="text-zinc-600 text-xs italic ml-1">{c.note}</div>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Corey/Christine speech bubble */}
      <div className="mb-6 flex items-start gap-4 fade-up">
        <CoreyAvatar size="md" />
        <div className="relative flex-1 industrial-card !p-4 border-l-4 border-l-amber-400 bg-gradient-to-r from-amber-500/5 to-transparent">
          <div className="absolute -left-2 top-6 w-3 h-3 bg-zinc-900 border-l border-b border-zinc-800 rotate-45" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-1">
            {guide.name} says
          </div>
          <div className="text-zinc-200 text-sm leading-relaxed">
            {s.is_advanced
              ? `Alright, this one's got some real risk to it — but you can absolutely handle it if you go slow. I've got you. Follow the steps and read every safety note twice.`
              : `You've totally got this. Give me ${s.estimated_time?.toLowerCase() || 'a few minutes'} and about ${s.estimated_cost_usd || '$10-30'} in parts. Let's roll.`}
          </div>
          {sponsorMatchesTrade && (
            <div className="text-zinc-500 text-[11px] mt-2 font-mono italic border-t border-zinc-800 pt-2">
              <Zap size={10} className="inline mb-0.5 text-amber-400" /> {guide.name} works at {SPONSOR.name} — call{' '}
              <a
                href={SPONSOR.phoneHref}
                onClick={() => trackSponsorClick('corey_caption', { trade: s.trade, problemId: problem.id })}
                className="text-amber-400 hover:text-amber-300 underline"
              >
                {SPONSOR.phone}
              </a>{' '}
              if you'd rather have {guide.pronoun} swing by.
            </div>
          )}
        </div>
      </div>

      {/* Corey Demo Video (Sora 2) */}
      <div data-testid="video-section" className="mb-8">
        {videoUrl ? (
          <div className="industrial-card !p-4 border-t-4 border-t-lime-400">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-mono text-xs uppercase tracking-widest text-lime-400 flex items-center gap-2">
                <Video size={14} /> {guide.name}'s Demo · 8 sec
              </div>
              <div className="flex items-center gap-3">
                <div className="font-handwritten text-lg text-amber-400 hidden sm:block">Watch, then do.</div>
                <button
                  data-testid="regenerate-video-btn"
                  onClick={() => setShowTuner(true)}
                  className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-400 font-mono text-[10px] uppercase tracking-widest border border-zinc-700 hover:border-amber-500 px-2 py-1 transition-colors"
                  title="Regenerate with different settings"
                >
                  <RefreshCw size={12} /> Try Again
                </button>
              </div>
            </div>
            <video
              data-testid="corey-video"
              key={videoUrl}
              src={videoUrl}
              controls
              playsInline
              muted
              className="w-full max-h-[420px] bg-black border border-zinc-800"
            />
            {showTuner && (
              <VideoTuner
                setting={vSetting}
                focus={vFocus}
                notes={vNotes}
                onSetting={setVSetting}
                onFocus={setVFocus}
                onNotes={setVNotes}
                onClose={() => setShowTuner(false)}
                onSubmit={() => generateVideo({ regenerate: true })}
                submitLabel="Regenerate"
                loading={false}
                warnCost
              />
            )}
          </div>
        ) : videoLoading ? (
          <div className="industrial-card !p-6 border-t-4 border-t-lime-400 text-center">
            <div className="flex justify-center mb-4">
              <CoreyAvatar size="lg" />
            </div>
            <Loader2 size={20} className="animate-spin text-lime-400 mx-auto mb-3" />
            <div className="font-display text-2xl text-zinc-100 tracking-wide mb-1">
              {guide.name.toUpperCase()} IS FILMING…
            </div>
            <div className="text-zinc-400 text-sm max-w-md mx-auto mb-3">
              Sora 2 is rendering your custom demo. Usually 2–4 minutes. You can safely walk away — the video will be here when you're back.
            </div>
            <div className="font-mono text-xs text-lime-400 uppercase tracking-widest">
              elapsed: {Math.floor(videoElapsedSec / 60)}:{String(videoElapsedSec % 60).padStart(2, '0')}
            </div>
          </div>
        ) : (
          <div className="industrial-card !p-5 border-l-4 border-l-lime-400 bg-gradient-to-r from-lime-500/5 to-transparent">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 bg-lime-400 text-zinc-950 flex items-center justify-center shrink-0 shadow-lg shadow-lime-500/30">
                  <Play size={22} strokeWidth={2.5} fill="currentColor" />
                </div>
                <div>
                  <div className="font-display text-xl text-zinc-100 tracking-wide leading-tight">
                    WANT {guide.name.toUpperCase()} TO SHOW YOU?
                  </div>
                  <div className="text-zinc-400 text-sm">
                    {guide.pronoun_cap}'ll film an 8-second custom demo of this exact fix. Takes ~2–4 min.
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  data-testid="tune-video-btn"
                  onClick={() => setShowTuner((v) => !v)}
                  className="inline-flex items-center gap-1 text-zinc-300 hover:text-amber-400 font-mono text-[11px] uppercase tracking-widest border border-zinc-700 hover:border-amber-500 px-3 py-2 transition-colors"
                >
                  <Sliders size={13} /> Tune
                </button>
                <button
                  data-testid="generate-video-btn"
                  onClick={() => generateVideo()}
                  className="btn-lime text-sm hover-wiggle whitespace-nowrap"
                >
                  <Video size={16} /> Film It, {guide.name}
                </button>
              </div>
            </div>
            {showTuner && (
              <VideoTuner
                setting={vSetting}
                focus={vFocus}
                notes={vNotes}
                onSetting={setVSetting}
                onFocus={setVFocus}
                onNotes={setVNotes}
                onClose={() => setShowTuner(false)}
                onSubmit={() => generateVideo()}
                submitLabel="Film It"
                loading={false}
              />
            )}
          </div>
        )}
        {videoError && (
          <div className="text-red-400 text-sm mt-2 font-mono flex items-center gap-2">
            <AlertTriangle size={14} /> {videoError}
          </div>
        )}
      </div>

      {/* Meta cards */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <MetaCard
          testid={IDS.solutionDifficulty}
          icon={Gauge}
          label="Difficulty"
          value={s.difficulty}
          accent={
            /begin|easy/i.test(s.difficulty || '') ? 'lime'
              : /adv|hard|expert|pro/i.test(s.difficulty || '') ? 'red'
              : 'amber'
          }
        />
        <MetaCard testid={IDS.solutionTime} icon={Clock} label="Time" value={s.estimated_time} accent="sky" />
        <MetaCard testid={IDS.solutionCost} icon={DollarSign} label="DIY Cost" value={s.estimated_cost_usd} accent="lime" />
      </div>

      {/* Tools & Materials - hidden by default, revealed on demand */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <RevealCard
          testid={IDS.solutionTools}
          icon={Wrench}
          title="Tools You'll Need"
          count={s.tools_needed?.length || 0}
          open={showTools}
          onToggle={() => setShowTools((v) => !v)}
        >
          {s.tools_needed?.length > 0 ? (
            <ul className="space-y-2 text-zinc-300 text-sm">
              {s.tools_needed.map((t, i) => (
                <li key={t.name || `tool-${i}`} className="flex items-start gap-2">
                  <span className="text-amber-500 font-mono mt-0.5">›</span>
                  <div className="flex-1">
                    <div>
                      <span className="text-zinc-100">{t.name}</span>
                      {t.optional && (
                        <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-700 px-1.5 py-0.5">
                          Optional
                        </span>
                      )}
                    </div>
                    {t.note && <div className="text-zinc-500 text-xs">{t.note}</div>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-zinc-500 text-sm italic">No special tools required</div>
          )}
        </RevealCard>

        <RevealCard
          testid={IDS.solutionMaterials}
          icon={Package}
          title="Materials & Prices"
          count={s.materials_needed?.length || 0}
          open={showMaterials}
          onToggle={() => setShowMaterials((v) => !v)}
        >
          {s.materials_needed?.length > 0 ? (
            <ul className="space-y-3 text-zinc-300 text-sm">
              {s.materials_needed.map((m, i) => (
                <li key={m.name || `mat-${i}`} className="border-l-2 border-zinc-800 pl-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-zinc-100 font-medium">
                      {m.name}
                      {m.quantity && (
                        <span className="ml-2 text-xs font-mono text-zinc-500">× {m.quantity}</span>
                      )}
                    </div>
                    {m.price_usd && (
                      <span className="inline-flex items-center gap-1 font-mono text-xs bg-amber-500/10 text-amber-500 border border-amber-500/30 px-2 py-0.5">
                        <Tag size={11} /> {m.price_usd}
                      </span>
                    )}
                  </div>
                  {m.where_to_buy && (
                    <div className="text-zinc-500 text-xs mt-1 flex items-center gap-1">
                      <MapPin size={11} /> {m.where_to_buy}
                    </div>
                  )}
                  {m.note && <div className="text-zinc-500 text-xs mt-0.5">{m.note}</div>}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-zinc-500 text-sm italic">No materials needed</div>
          )}
        </RevealCard>
      </div>

      {/* Safety warnings */}
      {s.safety_warnings?.length > 0 && (
        <div data-testid={IDS.solutionSafety} className="industrial-card border-l-4 border-l-amber-500 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-amber-500" />
            <div className="font-mono text-xs uppercase tracking-widest text-amber-500">Safety First</div>
          </div>
          <ul className="space-y-2 text-zinc-300 text-sm">
            {s.safety_warnings.map((w, i) => (
              <li key={typeof w === 'string' ? w : `safe-${i}`} className="flex gap-2">
                <span className="text-amber-500 font-mono">›</span> {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Steps */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-green-500 mb-1">
              /diy_route
            </div>
            <h2 className="font-display text-3xl text-zinc-100 tracking-tight">
              STEP-BY-STEP FIX
            </h2>
          </div>
          <div className="hidden sm:inline-block border border-green-500/40 text-green-500 px-3 py-1 font-mono text-xs uppercase tracking-widest">
            You've got this
          </div>
        </div>

        <ol data-testid={IDS.solutionSteps} className="space-y-3">
          {s.steps.map((step, i) => (
            <li
              key={step.step}
              data-testid={IDS.solutionStepItem}
              className="industrial-card flex gap-4 hover:border-lime-500/60 transition-colors"
              style={{ animation: `fadeUp 0.5s ease-out ${i * 60}ms both` }}
            >
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-amber-400 to-orange-500 text-zinc-950 font-display text-2xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                {step.step}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-zinc-100 mb-1">{step.title}</div>
                <div className="text-zinc-300 text-sm leading-relaxed">{step.detail}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>


      {/* Academy: Why This Works (subscription-locked) */}
      <TheorySection problemId={problem.id} />

      {/* Contact Electric spotlight if electrical */}
      {sponsorMatchesTrade && (        <div className="industrial-card border-t-4 border-t-amber-500 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between mb-6">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-amber-500 flex items-center justify-center shrink-0">
              <Zap size={22} className="text-zinc-950" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500 mb-1">
                Local Electrical Pro
              </div>
              <div className="font-display text-2xl text-zinc-100">{SPONSOR.name}</div>
              <div className="text-zinc-400 text-sm">{SPONSOR.tagline} · {SPONSOR.city}</div>
            </div>
          </div>
          <a
            href={SPONSOR.phoneHref}
            onClick={() => trackSponsorClick('solution_card', { trade: s.trade, problemId: problem.id })}
            className="btn-primary"
          >
            <PhoneCall size={16} /> Call {SPONSOR.phone}
          </a>
        </div>
      )}

      {/* Call a pro (bottom, playful) */}
      <div className="border-t border-zinc-800 pt-10 mt-10">
        <div className="industrial-card !p-8 border-t-4 border-t-lime-400 bg-gradient-to-br from-lime-500/10 via-sky-500/5 to-transparent glow-lime text-center">
          <div className="font-handwritten text-2xl text-lime-400 mb-2 rotate-[-1deg] inline-block">
            <Heart size={16} className="inline mb-1 fill-lime-400 pulse-gentle" /> No judgment here…
          </div>
          <h3 className="font-display text-3xl sm:text-4xl text-zinc-100 tracking-tight leading-tight mb-3">
            IN OVER YOUR HEAD?
          </h3>
          <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
            We'll find you a <span className="text-lime-400 font-semibold">top-rated, reasonably-priced</span> local pro. Your secret is safe with us.
          </p>
          <button
            data-testid={IDS.solutionCallProBtn}
            onClick={() => setShowProModal(true)}
            className="btn-lime text-sm sm:text-base hover-wiggle"
          >
            <Sparkles size={16} /> Don't Tell the Wife She Was Right
          </button>
          <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest mt-4">
            (finds 5-star pros near you)
          </div>
        </div>
      </div>

      {showProModal && (
        <CallProModal
          onClose={() => setShowProModal(false)}
          trade={s.trade}
          query={s.call_pro_search_query}
        />
      )}

      <AdvancedTools problemId={problem.id} />
    </div>
  );
}


function AdvancedTools({ problemId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.is_advanced) return;
    (async () => {
      try {
        const res = await api.get(`/problems/${problemId}/notes`);
        setNotes(res.data?.notes || []);
      } catch (e) { console.warn('Notes fetch failed:', e); }
      finally { setLoaded(true); }
    })();
  }, [user?.is_advanced, problemId]);

  if (!user?.is_advanced) return null;

  const addNote = async () => {
    const text = newNote.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      const res = await api.post(`/problems/${problemId}/notes`, { text });
      setNotes((prev) => [...prev, res.data]);
      setNewNote('');
    } catch (e) { console.warn('Add note failed:', e); }
    finally { setSaving(false); }
  };

  const removeNote = async (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/problems/${problemId}/notes/${id}`); }
    catch (e) { console.warn('Delete note failed:', e); }
  };

  return (
    <div data-testid="advanced-tools" className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
      <div className="industrial-card border-t-4 border-t-lime-400">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-lime-400 flex items-center gap-2">
              <Hammer size={14} /> Advanced Toolkit
            </div>
            <div className="font-display text-2xl sm:text-3xl text-zinc-100 tracking-tight mt-1">
              JOB NOTES & REPAIR REPORT
            </div>
          </div>
          <Link
            to={`/report/${problemId}`}
            data-testid="advanced-report-btn"
            className="btn-primary text-sm"
            target="_blank"
            rel="noopener"
          >
            <FileText size={14} /> Generate Repair Report
          </Link>
        </div>

        <div className="space-y-2 mb-3" data-testid="notes-list">
          {loaded && notes.length === 0 && (
            <div className="text-zinc-500 text-sm font-mono italic">
              No notes yet. Jot torque specs, wire colors, "left off at step 3", or anything worth remembering next time.
            </div>
          )}
          {notes.map((n) => (
            <div key={n.id} className="border border-zinc-800 bg-zinc-950/60 p-3 flex justify-between items-start gap-3">
              <div className="text-zinc-200 text-sm whitespace-pre-wrap flex-1">{n.text}</div>
              <button
                onClick={() => removeNote(n.id)}
                data-testid={`note-delete-${n.id}`}
                className="text-zinc-500 hover:text-red-400 shrink-0"
                title="Delete note"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <textarea
            data-testid="notes-input"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Note: torque 15 lb-ft · blue wire = neutral · left off at step 4…"
            className="industrial-textarea !min-h-[60px] text-sm flex-1"
            rows={2}
          />
          <button
            onClick={addNote}
            disabled={saving || !newNote.trim()}
            data-testid="notes-add"
            className="btn-lime text-sm sm:self-end"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} /> Add</>}
          </button>
        </div>
      </div>
    </div>
  );
}


const V_SETTINGS = ['Kitchen', 'Bathroom', 'Garage', 'Basement', 'Living Room', 'Bedroom', 'Outdoor', 'Anywhere'];
const V_FOCUS = [
  { value: 'mostly close-ups on his hands and the tools', label: 'Close-up on hands' },
  { value: 'wide shot showing his full stance and the whole workspace', label: 'Wide shot' },
  { value: 'a balanced mix of close-up hands and wider shots', label: 'Mix of both' },
];

function VideoTuner({ setting, focus, notes, onSetting, onFocus, onNotes, onClose, onSubmit, submitLabel = 'Generate', warnCost = false }) {
  return (
    <div className="mt-4 border border-zinc-800 bg-zinc-950/60 p-4 space-y-3 relative">
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-100 p-1"
        aria-label="Close tuner"
      >
        <X size={14} />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-2">
        <Sliders size={11} /> Video Tuner
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
            Setting
          </label>
          <select
            data-testid="video-tuner-setting"
            className="industrial-input !py-2 text-sm"
            value={setting}
            onChange={(e) => onSetting(e.target.value)}
          >
            <option value="">Auto (match the problem)</option>
            {V_SETTINGS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
            Camera Focus
          </label>
          <select
            data-testid="video-tuner-focus"
            className="industrial-input !py-2 text-sm"
            value={focus}
            onChange={(e) => onFocus(e.target.value)}
          >
            <option value="">Auto (best for the fix)</option>
            {V_FOCUS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
          Anything specific to show? (optional)
        </label>
        <input
          data-testid="video-tuner-notes"
          type="text"
          className="industrial-input !py-2 text-sm"
          placeholder='e.g. "make sure he wears safety glasses" or "start with the breaker box"'
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          maxLength={200}
        />
      </div>
      {warnCost && (
        <div className="text-amber-400/80 text-xs font-mono">
          ⚠ Regenerating uses fresh Sora 2 credits and takes another 2–4 min.
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-zinc-100 font-mono text-xs uppercase tracking-widest px-3 py-2"
        >
          Cancel
        </button>
        <button
          data-testid="video-tuner-submit"
          onClick={onSubmit}
          className="btn-lime text-sm"
        >
          <Video size={14} /> {submitLabel}
        </button>
      </div>
    </div>
  );
}

function MetaCard({ testid, icon: Icon, label, value, accent = 'amber' }) {  const accents = {
    amber: { text: 'text-amber-400', border: 'border-t-amber-400', glow: 'glow-amber' },
    sky: { text: 'text-sky-400', border: 'border-t-sky-400', glow: 'glow-sky' },
    lime: { text: 'text-lime-400', border: 'border-t-lime-400', glow: 'glow-lime' },
    red: { text: 'text-red-400', border: 'border-t-red-500', glow: '' },
  };
  const a = accents[accent] || accents.amber;
  return (
    <div data-testid={testid} className={`industrial-card p-4 border-t-2 ${a.border} ${a.glow} transition-all hover:-translate-y-0.5`}>
      <Icon size={18} className={`${a.text} mb-2`} strokeWidth={2} />
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-zinc-100 font-semibold mt-1 text-sm sm:text-base break-words">{value}</div>
    </div>
  );
}

function ListCard({ testid, icon: Icon, title, items }) {
  return (
    <div data-testid={testid} className="industrial-card">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-amber-500" strokeWidth={2} />
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-400">{title}</div>
      </div>
      {items?.length > 0 ? (
        <ul className="space-y-1.5 text-zinc-300 text-sm">
          {items.map((it, i) => (
            <li key={typeof it === 'string' ? it : `it-${i}`} className="flex gap-2">
              <span className="text-amber-500 font-mono">›</span> {it}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-zinc-500 text-sm italic">None required</div>
      )}
    </div>
  );
}

function RevealCard({ testid, icon: Icon, title, count, open, onToggle, children }) {
  return (
    <div data-testid={testid} className="industrial-card !p-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 hover:bg-zinc-900/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-amber-500" strokeWidth={2} />
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-100">{title}</div>
          {count > 0 && (
            <span className="text-[10px] font-mono text-amber-500 border border-amber-500/40 px-1.5 py-0.5">
              {count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-amber-500">
          <span className="font-mono text-[10px] uppercase tracking-widest hidden sm:inline">
            {open ? 'Hide' : 'Show'}
          </span>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>
      {open && <div className="border-t border-zinc-800 p-4">{children}</div>}
    </div>
  );
}
